import type { ReactNode } from "react";
import { ArrowRight, Plus } from "lucide-react";

const TONE_STYLES: Record<string, { bg: string; text: string }> = {
  brand: { bg: "bg-brand-50 dark:bg-primary/10", text: "text-brand-600 dark:text-primary" },
  emerald: { bg: "bg-emerald-50 dark:bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400" },
  amber: { bg: "bg-amber-50 dark:bg-amber-500/10", text: "text-amber-600 dark:text-amber-400" },
  sky: { bg: "bg-sky-50 dark:bg-sky-500/10", text: "text-sky-600 dark:text-sky-400" },
  fuchsia: { bg: "bg-fuchsia-50 dark:bg-fuchsia-500/10", text: "text-fuchsia-600 dark:text-fuchsia-400" },
  zinc: { bg: "bg-zinc-100 dark:bg-muted", text: "text-zinc-500 dark:text-muted-foreground" },
};
export type TomDoQuadro = keyof typeof TONE_STYLES;

/** Moldura de um quadro da aba Início da ficha. `menu` é o "⋯" de trocar o quadro. */
export function Card({
  icon,
  title,
  tone = "zinc",
  menu,
  children,
}: {
  icon: ReactNode;
  title: string;
  tone?: TomDoQuadro;
  menu?: ReactNode;
  children: ReactNode;
}) {
  const t = TONE_STYLES[tone] ?? TONE_STYLES.zinc;
  return (
    <section className="flex flex-col rounded-xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-5 dark:border-border dark:bg-card">
      <div className="mb-4 flex items-center gap-2.5">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${t.bg} ${t.text}`}>{icon}</div>
        <h4 className="min-w-0 flex-1 truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{title}</h4>
        {menu}
      </div>
      <div className="flex flex-1 flex-col">{children}</div>
    </section>
  );
}

export function CardLoading() {
  return (
    <div className="flex items-center justify-center py-10">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
    </div>
  );
}

export function CardEmpty({ icon, text, hint }: { icon: ReactNode; text: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100 dark:bg-muted">
        <span className="text-zinc-400">{icon}</span>
      </div>
      <p className="text-sm font-medium text-zinc-700 dark:text-foreground">{text}</p>
      {hint && <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Rodapé do quadro: link à esquerda, ação à direita, sempre no pé — quadros lado a lado alinham. */
export function Rodape({ children }: { children: ReactNode }) {
  return (
    <div className="mt-auto pt-3">
      <div className="flex items-center justify-between border-t border-zinc-100 pt-3 dark:border-border">{children}</div>
    </div>
  );
}

/** "Ver todos" que leva pra aba da ficha onde o assunto mora inteiro. */
export function VerTodos({ rotulo, onClick }: { rotulo: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-700 hover:text-brand-900 dark:text-primary"
    >
      {rotulo}
      <ArrowRight className="h-3 w-3" />
    </button>
  );
}

/** Ação principal do quadro, à direita do rodapé ("+ Agendar", "+ Novo pedido"...). */
export function AcaoDoRodape({ rotulo, onClick }: { rotulo: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-7 items-center gap-1 rounded-md bg-brand-600 px-2.5 text-[11px] font-medium text-white hover:bg-brand-700"
    >
      <Plus className="h-3 w-3" />
      {rotulo}
    </button>
  );
}

/** Linha clicável de uma lista dentro do quadro. */
export const linhaDoQuadro =
  "flex w-full items-start gap-3 rounded-lg border border-zinc-100 bg-white p-2.5 text-left transition-colors hover:border-brand-200 hover:bg-brand-50/30 dark:border-border dark:bg-card dark:hover:bg-primary/5";
